import { inject, Injectable } from '@angular/core';
import { ApiService, LOADING_STATUS } from './api.service';
import { Store } from '@ngrx/store';
import { catchError, filter, forkJoin, lastValueFrom, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ToasterService } from './toaster.service';
import {
  prefFilters,
  setFilters,
  setHardFilters,
  setProfile,
  setRecsBatchDate,
  setScreenAccess,
  setVoidBatchDate,
  setVoidFilters,
} from '@app/state/actions/app.action';
import { Filter, SELECTED_FILTER_VALUES } from '@app/types/filter.types';
import { UserProfile } from '@app/types/app.types';
import { RouteAccessService } from './route-access.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  appLoadingStatus: LOADING_STATUS = LOADING_STATUS.NOT_STARTED;
  hasApiFailed: boolean = false;
  private routeAccessService = inject(RouteAccessService);
  private store = inject(Store);
  private apiService = inject(ApiService);
  private toastService = inject(ToasterService);
  screenAccess: string[] = [];
  currentScreen: string = '';

  profile$!: Observable<UserProfile | null>;

  constructor() {}

  async fetchApplicationData(filtersObj: any, appURL: string): Promise<void> {
    const bottlerIdFilter = filtersObj.filter((filterItem: any) => filterItem.filterKey === 'bottlerId');
    const roleFilter = filtersObj.filter((filterItem: any) => filterItem.filterKey === 'highLevelTc');
    let shouldFetchScreenAccess = false;
    if (bottlerIdFilter && bottlerIdFilter.length > 0) {
      const bottlerIdValue = bottlerIdFilter.values;
      if (bottlerIdValue) {
        shouldFetchScreenAccess = true;
      }
      try {
        if (shouldFetchScreenAccess) {
          const screenAccessResponse = await lastValueFrom(
            this.apiService.getScreenAccess().pipe(
              catchError((err) => {
                console.error('Get Screen Access API failed after settings changed', err);
                return of({ data: { screen_access: [] } });
              }),
              tap((response) => {
                this.screenAccess = response.data.screen_access;
                this.store.dispatch(setScreenAccess({ screenAccessState: this.screenAccess }));
                console.log('Screen access updated after Settings changed:', this.screenAccess);
                for (const mapping of this.routeAccessService.routeMap) {
                  if (appURL === mapping.path) {
                    this.currentScreen = mapping.access;
                  }
                }
                if (!this.screenAccess.includes(this.currentScreen)) {
                  this.routeAccessService.redirectToDefaultPage(this.screenAccess);
                }
              }),
            ),
          );
        }
        if (this.screenAccess && this.screenAccess.includes('RECS')) {
          const filterRes = await lastValueFrom(
            this.apiService.getFilterValues().pipe(
              catchError((err) => {
                console.error('Filter Values API failed', err);
                return of({ data: [] });
              }),
            ),
          );
          const userPrefRes = await lastValueFrom(
            this.apiService.getUserPreference().pipe(
              catchError((err) => {
                console.error('User Preference API failed', err);
                return of({ data: [] });
              }),
            ),
          );
          this.storePreference(userPrefRes.data);
          this.storeFilters(filterRes.data);
          this.storePrefFilters(filterRes.data);
        }

        if (this.screenAccess && this.screenAccess.includes('VOIDS')) {
          const voidFilterRes = await lastValueFrom(
            this.apiService.getVoidFiltersValues().pipe(
              catchError((err) => {
                console.error('Void Filter API failed', err);
                return of({ data: [] });
              }),
            ),
          );
          this.storeVoidFilters(voidFilterRes.data);
        }
      } catch (error) {
        console.error('Application server API calls failed', error);
        this.handleApiError();
      }
    }

    if (roleFilter && roleFilter.length > 0) {
      const filterRes = await lastValueFrom(
        this.apiService.getFilterValues().pipe(
          catchError((err) => {
            console.error('Filter Values API failed', err);
            return of({ data: [] });
          }),
        ),
      );
      const userPrefRes = await lastValueFrom(
        this.apiService.getUserPreference().pipe(
          catchError((err) => {
            console.error('User Preference API failed', err);
            return of({ data: [] });
          }),
        ),
      );
      this.storePreference(userPrefRes.data);
      this.storeFilters(filterRes.data);
      this.storePrefFilters(filterRes.data);
      this.routeAccessService.redirectToDefaultPage(['RECS']);
    }
  }

  private handleApiError() {
    this.hasApiFailed = true;
    this.storePreference([]);
    this.storeFilters([]);
    this.storePrefFilters([]);
    this.appLoadingStatus = LOADING_STATUS.ERROR;
  }

  private storePreference(data: any) {
    const bottlerRole = data?.userDetail?.bottlerRole;
    if (bottlerRole) {
      sessionStorage.setItem('bottlerRole', bottlerRole);
    }
    const currentBottlerId = data?.userDetail?.bottlerId;
    if (currentBottlerId) {
      sessionStorage.setItem('currentBottlerId', currentBottlerId);
    }
    if (data?.preferences) {
      if (data?.preferences.hardFilters.length > 0) {
        const hardFilters: SELECTED_FILTER_VALUES[] = data?.preferences.hardFilters.map((preference: any) => {
          let filterKey = preference.filterKey;
          return { filterKey, values: preference.values };
        });
        this.store.dispatch(setHardFilters({ hardFilters }));
      } else {
        this.store.dispatch(setHardFilters({ hardFilters: [] }));
      }
    }
  }

  //old store filters
  private storePrefFilters(data: any) {
    console.log('store Filters method ', data);
    const filters: Filter[] = data.finalFilter.map((filter: any) => {
      const copyOfFilter = { ...filter };
      copyOfFilter.default_options = copyOfFilter.default_options
        .map((option: any) => {
          const copyOfOption = { ...option }; // Make a shallow copy of the option

          if (Array.isArray(copyOfOption.metadata) && copyOfOption.metadata[0]) {
            const output = Object.entries(copyOfOption.metadata[0]).reduce((acc, [key, values]) => {
              (values as string[]).forEach((value) => acc.push(`${key}_${value}`));
              return acc;
            }, [] as string[]);
            copyOfOption.metadata = output;
          }

          return copyOfOption;
        })
        .sort((a: any, b: any) => {
          return a.label.localeCompare(b.label);
        });

      return copyOfFilter;
    });
    this.store.dispatch(setRecsBatchDate({ recsBatchDate: data.batchNo }));
    this.store.dispatch(prefFilters({ prefFilters: filters }));
  }

  //stores data exactly how it receives from api
  private storeFilters(data: any) {
    console.log('store Filters method ', data);
    const filters: Filter[] = data.finalFilter.map((filter: any) => {
      const copyOfFilter = { ...filter };
      return copyOfFilter;
    });
    this.store.dispatch(setRecsBatchDate({ recsBatchDate: data.batchNo }));
    this.store.dispatch(setFilters({ filters }));
  }

  private storeVoidFilters(data: any) {
    const filters: Filter[] = data.finalFilter.map((filter: any) => {
      const copyOfFilter = { ...filter };
      copyOfFilter.default_options = copyOfFilter.default_options
        .map((option: any) => {
          if (Array.isArray(option.metadata) && option.metadata[0]) {
            const output = Object.entries(option.metadata[0]).reduce((acc, [key, values]) => {
              (values as string[]).forEach((value) => acc.push(`${key}_${value}`));
              return acc;
            }, [] as string[]);
            option.metadata = output;
          }
          return option;
        })
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      return copyOfFilter;
    });
    this.store.dispatch(setVoidBatchDate({ voidBatchDate: data.batchDate }));
    this.store.dispatch(setVoidFilters({ filters }));
  }
}
